// src/components/PrintDesigner/DesignerCanvas.jsx
// لوحة التصميم: الصفحة بنطاقاتها + سحب العناصر وتحريكها وتغيير حجمها + خطوط المحاذاة
import React, { useRef, useState, useEffect } from "react";
import { ElementView, ItemsTable } from "./PrintLayoutRenderer";
import { PAPER, contentWidth } from "./printTemplates";

const PX_PER_MM = 96 / 25.4;
const ACCENT = "#4F46E5";
const SNAP_EDGE = 1.5; // مم — مسافة الالتصاق بحواف العناصر الأخرى
export const DRAG_MIME = "application/x-print-element";

const mm = (v) => `${v}mm`;
const round = (v) => Math.round(v * 10) / 10;
const HANDLES = ["n", "s", "e", "w", "ne", "nw", "se", "sw"];
const cursorOf = { n: "ns-resize", s: "ns-resize", e: "ew-resize", w: "ew-resize", ne: "nesw-resize", sw: "nesw-resize", nw: "nwse-resize", se: "nwse-resize" };

const BAND_LABEL = { header: "رأس الصفحة", footer: "ذيل الصفحة" };

export default function DesignerCanvas({
  layout, data, zoom, showGrid, snap, selectedId, editingId,
  onSelect, onEdit, onChangeElement, onGestureStart, onGestureEnd, onDropNew, onBandHeight,
}) {
  const ppm = PX_PER_MM * zoom;
  const paper = PAPER[layout.page.size] || PAPER.A4;
  const margin = layout.page.margin ?? 8;
  const W = contentWidth(layout);
  const [guides, setGuides] = useState([]); // [{band, axis:'x'|'y', at}]
  const [dropBand, setDropBand] = useState(null);

  const snapV = (v) => (snap ? Math.round(v) : round(v));

  // ── التقاط حواف العناصر الأخرى ──
  const magnet = (el, nx, ny, nw, nh) => {
    const others = layout.elements.filter(o => o.band === el.band && o.id !== el.id);
    const xs = [0, W / 2, W, ...others.flatMap(o => [o.x, o.x + o.w / 2, o.x + o.w])];
    const bandH = layout.bands[el.band].height;
    const ys = [0, bandH, ...others.flatMap(o => [o.y, o.y + o.h / 2, o.y + o.h])];
    const g = [];
    const tryAxis = (pos, size, targets, axis) => {
      for (const [off] of [[0], [size / 2], [size]]) {
        const hit = targets.find(t => Math.abs(pos + off - t) <= SNAP_EDGE);
        if (hit != null) { g.push({ band: el.band, axis, at: hit }); return round(hit - off); }
      }
      return pos;
    };
    return { x: tryAxis(nx, nw, xs, "x"), y: tryAxis(ny, nh, ys, "y"), guides: g };
  };

  // ── تحريك ──
  const startMove = (e, el) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    onSelect(el.id);
    if (el.locked || editingId === el.id) return;
    const sx = e.clientX, sy = e.clientY, ox = el.x, oy = el.y;
    let moved = false;
    const onMove = (ev) => {
      const dx = (ev.clientX - sx) / ppm, dy = (ev.clientY - sy) / ppm;
      if (!moved && Math.abs(dx) + Math.abs(dy) < 0.4) return;
      if (!moved) { moved = true; onGestureStart(); }
      let nx = snapV(ox + dx), ny = snapV(oy + dy);
      const m = ev.altKey ? { x: nx, y: ny, guides: [] } : magnet(el, nx, ny, el.w, el.h);
      setGuides(m.guides);
      nx = Math.max(-el.w + 2, Math.min(W - 2, m.x));
      ny = Math.max(0, Math.min(layout.bands[el.band].height - 1, m.y));
      onChangeElement(el.id, { x: nx, y: ny }, true);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setGuides([]);
      if (moved) onGestureEnd();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  // ── تغيير الحجم ──
  const startResize = (e, el, dir) => {
    e.stopPropagation();
    e.preventDefault();
    const sx = e.clientX, sy = e.clientY, o = { x: el.x, y: el.y, w: el.w, h: el.h };
    onGestureStart();
    const onMove = (ev) => {
      const dx = (ev.clientX - sx) / ppm, dy = (ev.clientY - sy) / ppm;
      let { x, y, w, h } = o;
      if (dir.includes("e")) w = o.w + dx;
      if (dir.includes("s")) h = o.h + dy;
      if (dir.includes("w")) { x = o.x + dx; w = o.w - dx; }
      if (dir.includes("n")) { y = o.y + dy; h = o.h - dy; }
      const min = el.type === "line" ? 0.5 : 2;
      if (w < min) { if (dir.includes("w")) x -= min - w; w = min; }
      if (h < min) { if (dir.includes("n")) y -= min - h; h = min; }
      onChangeElement(el.id, { x: snapV(x), y: snapV(Math.max(0, y)), w: snapV(w), h: snapV(h) }, true);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      onGestureEnd();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  // ── ارتفاع النطاق ──
  const startBandResize = (e, band) => {
    e.stopPropagation();
    e.preventDefault();
    const sy = e.clientY, oh = layout.bands[band].height;
    const minH = Math.max(5, ...layout.elements.filter(x => x.band === band).map(x => x.y + Math.min(x.h, 3)));
    onGestureStart();
    const onMove = (ev) => onBandHeight(band, Math.max(minH, snapV(oh + (ev.clientY - sy) / ppm)), true);
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      onGestureEnd();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  // ── الإفلات من صندوق الأدوات ──
  const onDrop = (e, band) => {
    e.preventDefault();
    setDropBand(null);
    const raw = e.dataTransfer.getData(DRAG_MIME);
    if (!raw) return;
    const r = e.currentTarget.getBoundingClientRect();
    onDropNew(band, snapV((e.clientX - r.left) / ppm), snapV((e.clientY - r.top) / ppm), JSON.parse(raw));
  };

  const gridBg = showGrid ? {
    backgroundImage: `linear-gradient(to right, rgba(79,70,229,.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(79,70,229,.08) 1px, transparent 1px)`,
    backgroundSize: `5mm 5mm`,
  } : {};

  const renderBand = (band) => {
    const H = layout.bands[band].height;
    const els = layout.elements.filter(x => x.band === band).sort((a, b) => (a.z || 0) - (b.z || 0));
    return (
      <div style={{ position: "relative" }}>
        <BandTag label={BAND_LABEL[band]} zoom={zoom} />
        <div
          data-band={band}
          onPointerDown={() => onSelect(null)}
          onDragOver={(e) => { if (e.dataTransfer.types.includes(DRAG_MIME)) { e.preventDefault(); setDropBand(band); } }}
          onDragLeave={() => setDropBand(null)}
          onDrop={(e) => onDrop(e, band)}
          style={{
            position: "relative", height: mm(H), width: mm(W),
            outline: `1px dashed ${dropBand === band ? ACCENT : "rgba(148,163,184,.7)"}`,
            background: dropBand === band ? "rgba(79,70,229,.05)" : "transparent",
            ...gridBg,
          }}
        >
          {els.map(el => {
            const sel = selectedId === el.id;
            const editing = editingId === el.id;
            return (
              <div
                key={el.id}
                onPointerDown={(e) => startMove(e, el)}
                onDoubleClick={(e) => { e.stopPropagation(); if (el.type === "text" && !el.locked) onEdit(el.id); }}
                style={{
                  position: "absolute", left: mm(el.x), top: mm(el.y), width: mm(el.w), height: mm(el.h),
                  zIndex: (el.z || 1) + (sel ? 1000 : 0),
                  cursor: el.locked ? "default" : editing ? "text" : "move",
                  outline: sel ? `${1.5 / zoom}px solid ${ACCENT}` : "none",
                  outlineOffset: 0,
                  touchAction: "none",
                }}
                className="pd-el"
              >
                {editing ? (
                  <InlineEditor el={el} onDone={(text) => { onEdit(null); if (text != null) onChangeElement(el.id, { props: { ...el.props, text } }, false); }} />
                ) : (
                  <ElementView el={el} data={data} design />
                )}
                {sel && !el.locked && !editing && HANDLES.map(h => (
                  <span
                    key={h}
                    onPointerDown={(e) => startResize(e, el, h)}
                    style={{
                      position: "absolute", width: 8 / zoom, height: 8 / zoom, background: "#fff",
                      border: `${1.5 / zoom}px solid ${ACCENT}`, borderRadius: 2 / zoom, cursor: cursorOf[h], zIndex: 2,
                      ...(h.includes("n") && { top: -4 / zoom }), ...(h.includes("s") && { bottom: -4 / zoom }),
                      ...(h.includes("w") && { left: -4 / zoom }), ...(h.includes("e") && { right: -4 / zoom }),
                      ...((h === "n" || h === "s") && { left: `calc(50% - ${4 / zoom}px)` }),
                      ...((h === "e" || h === "w") && { top: `calc(50% - ${4 / zoom}px)` }),
                    }}
                  />
                ))}
                {sel && el.locked && <LockBadge zoom={zoom} />}
              </div>
            );
          })}
          {guides.filter(g => g.band === band).map((g, i) => (
            <div key={i} style={{
              position: "absolute", pointerEvents: "none", background: "#ec4899", zIndex: 3000,
              ...(g.axis === "x" ? { left: mm(g.at), top: 0, bottom: 0, width: 1 / zoom } : { top: mm(g.at), left: 0, right: 0, height: 1 / zoom }),
            }} />
          ))}
        </div>
        {/* مقبض ارتفاع النطاق */}
        <div
          onPointerDown={(e) => startBandResize(e, band)}
          title="اسحب لتغيير ارتفاع النطاق"
          style={{ height: 6 / zoom, cursor: "ns-resize", background: "rgba(79,70,229,.18)", borderRadius: 3 / zoom, margin: `${1 / zoom}px 0` }}
        />
      </div>
    );
  };

  const tableSel = selectedId === "table";

  return (
    <div style={{ width: mm(paper.w * zoom), margin: "0 auto" }}>
      <div
        style={{
          width: mm(paper.w), transform: `scale(${zoom})`, transformOrigin: "top left",
          background: "#fff", boxShadow: "0 10px 30px -10px rgba(15,23,42,.35)",
          padding: mm(margin), boxSizing: "border-box", direction: "ltr",
          minHeight: paper.h ? mm(paper.h) : undefined,
          fontSize: `${layout.page.fontSize || 11}px`, color: "#111827",
        }}
      >
        {renderBand("header")}
        {layout.table?.enabled !== false && (
        <div
          onPointerDown={(e) => { e.stopPropagation(); onSelect("table"); }}
          style={{ position: "relative", margin: "1.5mm 0", cursor: "pointer", outline: tableSel ? `${1.5 / zoom}px solid ${ACCENT}` : "1px dashed rgba(148,163,184,.7)" }}
        >
          <BandTag label="جدول الأصناف (يتمدد حسب عدد الأصناف)" zoom={zoom} />
          <ItemsTable table={layout.table} data={data} sampleRows={3} />
        </div>
        )}
        {renderBand("footer")}
      </div>
    </div>
  );
}

function BandTag({ label, zoom }) {
  return (
    <div style={{
      position: "absolute", top: 0, right: "100%", marginRight: 6 / zoom, whiteSpace: "nowrap",
      fontSize: 10 / zoom, fontWeight: 700, color: "#64748b", fontFamily: "inherit", direction: "rtl", pointerEvents: "none",
    }}>{label}</div>
  );
}

function LockBadge({ zoom }) {
  return (
    <span style={{ position: "absolute", top: -14 / zoom, left: 0, fontSize: 10 / zoom, background: ACCENT, color: "#fff", padding: `0 ${4 / zoom}px`, borderRadius: 3 / zoom }}>
      مقفل
    </span>
  );
}

// تحرير النص مباشرة على الصفحة (نقر مزدوج)
function InlineEditor({ el, onDone }) {
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  const p = el.props;
  return (
    <textarea
      ref={ref}
      defaultValue={p.text}
      dir={p.dir || "rtl"}
      onPointerDown={(e) => e.stopPropagation()}
      onBlur={(e) => onDone(e.target.value)}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Escape") onDone(null);
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) e.currentTarget.blur();
      }}
      style={{
        width: "100%", height: "100%", boxSizing: "border-box", resize: "none", border: "none", outline: "none",
        background: "#fffbeb", fontFamily: "inherit", fontSize: `${p.fontSize || 12}px`, fontWeight: p.bold ? 800 : 500,
        textAlign: p.align || "right", lineHeight: p.lineHeight || 1.5, padding: mm(p.padding ?? 1), color: p.color || "#111827",
      }}
    />
  );
}
